import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Search, RefreshCw, Check, Pencil, ExternalLink } from "lucide-react";
import api from "@/lib/api";

function Row({ t, onSaved }) {
  const [text, setText] = useState(t.text);
  const [busy, setBusy] = useState(false);
  const dirty = text.trim() !== t.text;
  const run = async (fn, msg) => {
    setBusy(true);
    try { const { data } = await fn(); setText(data.text); onSaved(data); toast.success(msg); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not save"); } finally { setBusy(false); }
  };
  return (
    <tr className="border-t border-slate-100 align-top" data-testid={`tr-row-${t.id}`}>
      <td className="py-3 pr-4 text-sm text-[#071b22] w-[42%] whitespace-pre-line">{t.source}</td>
      <td className="py-2 pr-3">
        <textarea value={text} onChange={(e) => setText(e.target.value)} rows={Math.min(4, Math.ceil(t.source.length / 60) || 1)}
          className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-[#165a71] focus:outline-none" data-testid={`tr-text-${t.id}`} />
      </td>
      <td className="py-3 pr-3 w-24">
        <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold ${t.status === "edited" ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-600"}`} data-testid={`tr-status-${t.id}`}>
          {t.status === "edited" ? <><Pencil className="h-3 w-3" />Edited</> : "AI"}
        </span>
      </td>
      <td className="py-2 w-40 whitespace-nowrap">
        <button disabled={!dirty || busy} onClick={() => run(() => api.put(`/admin/i18n/${t.id}`, { text }), "Translation saved")}
          className="inline-flex items-center gap-1 rounded-lg bg-[#165a71] px-3 py-1.5 text-xs font-semibold text-white disabled:opacity-40" data-testid={`tr-save-${t.id}`}><Check className="h-3.5 w-3.5" />Save</button>
        <button disabled={busy} onClick={() => run(() => api.post(`/admin/i18n/${t.id}/retranslate`), "Re-translated by AI")} title="Ask the AI again"
          className="ml-1 inline-flex items-center gap-1 rounded-lg border border-slate-200 px-2.5 py-1.5 text-xs font-semibold text-slate-600 hover:bg-slate-50 disabled:opacity-40" data-testid={`tr-retranslate-${t.id}`}><RefreshCw className={`h-3.5 w-3.5 ${busy ? "animate-spin" : ""}`} /></button>
      </td>
    </tr>
  );
}

export default function AdminTranslationsPage() {
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [page, setPage] = useState(1);
  const [data, setData] = useState(null);
  useEffect(() => {
    const t = setTimeout(() => api.get("/admin/i18n", { params: { q: q || undefined, status: status || undefined, page } })
      .then((r) => setData(r.data)).catch(() => toast.error("Could not load translations")), 250);
    return () => clearTimeout(t);
  }, [q, status, page]);
  const pages = data ? Math.max(1, Math.ceil(data.total / data.page_size)) : 1;
  const patch = (row) => setData((d) => ({ ...d, items: d.items.map((x) => (x.id === row.id ? row : x)) }));
  return (
    <div className="max-w-6xl" data-testid="admin-translations-page">
      <div className="text-xs font-bold tracking-[0.2em] uppercase text-[#165a71]">Website</div>
      <h1 className="mt-2 font-display text-3xl md:text-4xl font-semibold text-[#071b22]">Spanish translations</h1>
      <p className="mt-2 max-w-3xl text-slate-500">
        English is the main language. When a visitor switches to <b>ES</b>, any text on the page is translated by AI once and saved here. Edit any line to correct it, and your version is used everywhere from then on.
        Content people write themselves (provider bios, listings, reviews, messages) stays in its original language.
      </p>
      <a href="/es" target="_blank" rel="noreferrer" className="mt-3 inline-flex items-center gap-1.5 text-sm font-semibold text-[#165a71] hover:underline" data-testid="tr-preview-link">Open the Spanish site <ExternalLink className="h-3.5 w-3.5" /></a>
      <div className="mt-6 flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[240px]">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <input value={q} onChange={(e) => { setQ(e.target.value); setPage(1); }} placeholder="Search English or Spanish text"
            className="w-full rounded-xl border border-slate-200 bg-white py-2.5 pl-9 pr-3 text-sm focus:border-[#165a71] focus:outline-none" data-testid="tr-search" />
        </div>
        <select value={status} onChange={(e) => { setStatus(e.target.value); setPage(1); }} className="rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm" data-testid="tr-status-filter">
          <option value="">All ({data ? data.counts.auto + data.counts.edited : "…"})</option>
          <option value="auto">AI translated ({data?.counts.auto ?? "…"})</option>
          <option value="edited">Edited by you ({data?.counts.edited ?? "…"})</option>
        </select>
      </div>
      <div className="mt-4 overflow-x-auto rounded-2xl border border-slate-200 bg-white px-4">
        <table className="w-full min-w-[760px]">
          <thead><tr className="text-left text-xs uppercase tracking-wider text-slate-400"><th className="py-3">English</th><th>Spanish</th><th>Source</th><th /></tr></thead>
          <tbody>
            {data?.items.map((t) => <Row key={t.id} t={t} onSaved={patch} />)}
            {data && !data.items.length && <tr><td colSpan={4} className="py-10 text-center text-sm text-slate-500" data-testid="tr-empty">No translations yet. Open the Spanish site and browse a few pages to create them.</td></tr>}
          </tbody>
        </table>
      </div>
      <div className="mt-4 flex items-center justify-between text-sm text-slate-500">
        <span data-testid="tr-total">{data?.total ?? 0} lines</span>
        <div className="flex items-center gap-2">
          <button disabled={page <= 1} onClick={() => setPage(page - 1)} className="rounded-lg border border-slate-200 px-3 py-1.5 disabled:opacity-40" data-testid="tr-prev">Previous</button>
          <span>Page {page} of {pages}</span>
          <button disabled={page >= pages} onClick={() => setPage(page + 1)} className="rounded-lg border border-slate-200 px-3 py-1.5 disabled:opacity-40" data-testid="tr-next">Next</button>
        </div>
      </div>
    </div>
  );
}
